import { Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { AdminService } from '../../core/services/admin.service';
import { AdminAnalyticsDto } from '../../core/models/admin.models';
import {
  LucideAngularModule,
  Users,
  TrendingUp,
  AlertCircle,
  Clock,
  DollarSign,
  UserPlus,
} from 'lucide-angular';

@Component({
  selector: 'app-admin-dashboard',
  standalone: true,
  imports: [CommonModule, RouterModule, LucideAngularModule],
  template: `
    <div class="p-6 space-y-6">
      <div class="flex items-center justify-between">
        <h1 class="text-2xl font-bold text-gray-900 dark:text-white">Admin Panel</h1>
        <a
          routerLink="/admin/customers"
          class="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
        >
          <lucide-icon [img]="UserPlus" class="w-4 h-4"></lucide-icon>
          Yangi mijoz
        </a>
      </div>

      @if (loading()) {
        <div class="grid grid-cols-2 lg:grid-cols-4 gap-4">
          @for (i of [1, 2, 3, 4]; track i) {
            <div class="bg-white dark:bg-gray-800 rounded-xl p-5 animate-pulse h-28"></div>
          }
        </div>
      } @else if (analytics()) {
        <!-- Stats Cards -->
        <div class="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div
            class="bg-white dark:bg-gray-800 rounded-xl p-5 border border-gray-100 dark:border-gray-700"
          >
            <div class="flex items-center justify-between mb-3">
              <span class="text-sm text-gray-500 dark:text-gray-400">Jami mijozlar</span>
              <div
                class="w-9 h-9 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center"
              >
                <lucide-icon
                  [img]="Users"
                  class="w-4 h-4 text-green-600 dark:text-green-400"
                ></lucide-icon>
              </div>
            </div>
            <p class="text-3xl font-bold text-gray-900 dark:text-white">
              {{ analytics()!.totalCustomers }}
            </p>
          </div>

          <div
            class="bg-white dark:bg-gray-800 rounded-xl p-5 border border-gray-100 dark:border-gray-700"
          >
            <div class="flex items-center justify-between mb-3">
              <span class="text-sm text-gray-500 dark:text-gray-400">Faol mijozlar</span>
              <div
                class="w-9 h-9 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center"
              >
                <lucide-icon
                  [img]="TrendingUp"
                  class="w-4 h-4 text-green-600 dark:text-green-400"
                ></lucide-icon>
              </div>
            </div>
            <p class="text-3xl font-bold text-green-600 dark:text-green-400">
              {{ analytics()!.activeCustomers }}
            </p>
          </div>

          <div
            class="bg-white dark:bg-gray-800 rounded-xl p-5 border border-gray-100 dark:border-gray-700"
          >
            <div class="flex items-center justify-between mb-3">
              <span class="text-sm text-gray-500 dark:text-gray-400">Demo mijozlar</span>
              <div
                class="w-9 h-9 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center"
              >
                <lucide-icon
                  [img]="Users"
                  class="w-4 h-4 text-blue-600 dark:text-blue-400"
                ></lucide-icon>
              </div>
            </div>
            <p class="text-3xl font-bold text-blue-600 dark:text-blue-400">
              {{ analytics()!.totalDemoCustomers }}
            </p>
          </div>

          <div
            class="bg-white dark:bg-gray-800 rounded-xl p-5 border border-gray-100 dark:border-gray-700"
          >
            <div class="flex items-center justify-between mb-3">
              <span class="text-sm text-gray-500 dark:text-gray-400">Muddati o'tgan</span>
              <div
                class="w-9 h-9 bg-red-100 dark:bg-red-900/30 rounded-lg flex items-center justify-center"
              >
                <lucide-icon
                  [img]="AlertCircle"
                  class="w-4 h-4 text-red-600 dark:text-red-400"
                ></lucide-icon>
              </div>
            </div>
            <p class="text-3xl font-bold text-red-600 dark:text-red-400">
              {{ analytics()!.expiredSubscriptions }}
            </p>
          </div>

          <div
            class="bg-white dark:bg-gray-800 rounded-xl p-5 border border-gray-100 dark:border-gray-700"
          >
            <div class="flex items-center justify-between mb-3">
              <span class="text-sm text-gray-500 dark:text-gray-400">Oylik tushum</span>
              <div
                class="w-9 h-9 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center"
              >
                <lucide-icon
                  [img]="DollarSign"
                  class="w-4 h-4 text-green-600 dark:text-green-400"
                ></lucide-icon>
              </div>
            </div>
            <p class="text-2xl font-bold text-green-600 dark:text-green-400">
              {{ analytics()!.totalMonthlyRevenue | number: '1.0-0' }} UZS
            </p>
          </div>
        </div>

        <!-- Expiring Soon Alert -->
        @if (analytics()!.expiringIn7Days > 0) {
          <div
            class="flex items-center gap-3 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-xl"
          >
            <lucide-icon
              [img]="Clock"
              class="w-5 h-5 text-yellow-600 dark:text-yellow-400 shrink-0"
            ></lucide-icon>
            <p class="text-sm text-yellow-800 dark:text-yellow-200">
              <strong>{{ analytics()!.expiringIn7Days }} ta mijoz</strong> tarifining muddati 7 kun
              ichida tugaydi.
              <a
                routerLink="/admin/customers"
                [queryParams]="{ subscriptionExpired: false }"
                class="underline ml-1"
                >Ko'rish</a
              >
            </p>
          </div>
        }

        <!-- Plan Distribution -->
        <div
          class="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 p-4 sm:p-5"
        >
          <h2 class="text-sm sm:text-base font-semibold text-gray-900 dark:text-white mb-4">
            Tariflar bo'yicha taqsimot
          </h2>
          <div class="space-y-4">
            @for (plan of analytics()!.planDistribution; track plan.planName; let i = $index) {
              <div>
                <div class="flex items-center justify-between mb-1.5 gap-2">
                  <div class="flex items-center gap-2 min-w-0">
                    <span
                      class="w-2.5 h-2.5 rounded-full shrink-0"
                      [style.background-color]="planColor(i)"
                    ></span>
                    <span class="text-sm font-medium text-gray-700 dark:text-gray-300 truncate">{{
                      plan.planName
                    }}</span>
                  </div>
                  <div class="flex items-center gap-2 shrink-0">
                    <span class="text-xs text-gray-400">{{ plan.count }} ta</span>
                    <span
                      class="text-sm font-semibold text-gray-800 dark:text-gray-100 tabular-nums whitespace-nowrap"
                    >
                      {{ fmtRevenue(plan.revenue) }}
                    </span>
                  </div>
                </div>
                <div class="h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                  <div
                    class="h-2 rounded-full transition-all duration-500"
                    [style.width.%]="getPercent(plan.count)"
                    [style.background-color]="planColor(i)"
                  ></div>
                </div>
              </div>
            }
          </div>
        </div>
      }
    </div>
  `,
})
export class AdminDashboardComponent implements OnInit {
  private adminService = inject(AdminService);
  private readonly destroyRef = inject(DestroyRef);

  analytics = signal<AdminAnalyticsDto | null>(null);
  loading = signal(true);

  Users = Users;
  TrendingUp = TrendingUp;
  AlertCircle = AlertCircle;
  Clock = Clock;
  DollarSign = DollarSign;
  UserPlus = UserPlus;

  ngOnInit() {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    this.adminService
      .getAnalytics({
        dateFrom: firstDay.toISOString().split('T')[0],
        dateTo: now.toISOString().split('T')[0],
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          if (res.success) this.analytics.set(res.data);
          this.loading.set(false);
        },
        error: () => this.loading.set(false),
      });
  }

  getPercent(count: number): number {
    const total = this.analytics()?.totalCustomers || 1;
    return Math.round((count / total) * 100);
  }

  private readonly PALETTE = [
    '#6366f1',
    '#22c55e',
    '#f59e0b',
    '#ef4444',
    '#8b5cf6',
    '#06b6d4',
    '#f97316',
    '#ec4899',
  ];

  planColor(index: number): string {
    return this.PALETTE[index % this.PALETTE.length];
  }

  fmtRevenue(amount: number): string {
    if (amount >= 1_000_000_000) return (amount / 1_000_000_000).toFixed(1) + ' mlrd';
    if (amount >= 1_000_000) return (amount / 1_000_000).toFixed(1) + ' mln';
    if (amount >= 1_000) return (amount / 1_000).toFixed(0) + ' K';
    return amount.toLocaleString();
  }
}
